/**
 * Language Picker Components
 *
 * Shared language selection used in search filter chips:
 * - Languages they speak (languages[])
 * - Languages they are learning (learn[])
 *
 * Pattern: inline chips + "+ Add" button → opens shared modal picker.
 * Clicking a language in the modal immediately adds it and closes.
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { getCountryCode } from './lang-country-map.js';
import { normalizeCode, normalizeChipItems } from './chip-normalize.js';

/**
 * Inline language chips component.
 * Renders selected languages as chips with an "+ Add" button.
 *
 * Usage:
 *   x-data="languageChips('languages[]')"
 *   x-init="init(languagesData, allLangsMap, suggestions)"
 */
export function languageChips(inputName) {
    return {
        inputName,
        languages: [],      // [{code, name}]
        allLanguages: {},   // {CODE: 'Name', ...}
        suggestions: [],    // [{code, name}]

        init(selected, all, suggestions) {
            this.allLanguages = all || {};
            this.languages = normalizeChipItems(selected, this.allLanguages).map(item => ({
                ...item,
                countryCode: getCountryCode(item.code),
            }));
            this.suggestions = normalizeChipItems(suggestions, this.allLanguages).map(item => ({
                ...item,
                countryCode: getCountryCode(item.code),
            }));
        },

        getCountryCode(code) {
            return getCountryCode(code);
        },

        removeLanguage(code) {
            // Bail on un-normalisable input: there is no canonical code to remove and
            // emitting any side effect (filter, event) for a value that could not have
            // been in the chip list would only confuse downstream listeners.
            const target = normalizeCode(code);
            if (!target) return;
            this.languages = this.languages.filter(l => l.code !== target);
        },

        addLanguage(code) {
            const normalized = normalizeCode(code);
            if (!normalized) return;
            if (this.languages.some(l => l.code === normalized)) return;
            this.languages.push({
                code: normalized,
                name: this.allLanguages[normalized] || normalized,
                countryCode: getCountryCode(normalized),
            });
        },

        get filteredSuggestions() {
            const selectedCodes = new Set(this.languages.map(l => l.code));
            return this.suggestions.filter(s => !selectedCodes.has(s.code)).slice(0, 3);
        },

        openModal() {
            const selectedCodes = this.languages.map(l => l.code);
            const self = this;
            this.$dispatch('open-language-picker', {
                allLanguages: this.allLanguages,
                selectedCodes,
                onSelect(code) {
                    self.addLanguage(code);
                }
            });
        }
    };
}

/**
 * Language picker modal component.
 * Opened via `@open-language-picker.window`.
 * Clicking a language immediately adds it and closes the modal.
 */
export function languagePickerModal() {
    return {
        isOpen: false,
        searchText: '',
        allLanguages: [],   // [{code, name}] sorted
        selectedCodes: [],
        onSelectCallback: null,

        init() {
            window.addEventListener('open-language-picker', (e) => {
                this.open(e.detail);
            });
        },

        getCountryCode(code) {
            return getCountryCode(code);
        },

        open(detail) {
            const map = detail.allLanguages || {};
            this.allLanguages = Object.entries(map)
                .map(([code, name]) => {
                    const canonical = normalizeCode(code);
                    if (!canonical) return null;
                    return { code: canonical, name, countryCode: getCountryCode(canonical) };
                })
                .filter(Boolean)
                .sort((a, b) => a.name.localeCompare(b.name));

            this.selectedCodes = (detail.selectedCodes || [])
                .map(normalizeCode)
                .filter(Boolean);
            this.onSelectCallback = detail.onSelect || null;
            this.searchText = '';
            this.isOpen = true;
            document.body.classList.add('picker-modal-open');
            lockBodyScroll();
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const input = this.$refs.languageSearch;
                if (input) input.focus();
            });
        },

        close() {
            this.isOpen = false;
            document.body.classList.remove('picker-modal-open');
            unlockBodyScroll();
        },

        destroy() {
            document.body.classList.remove('picker-modal-open');
        },

        get filteredLanguages() {
            const term = this.searchText.toLowerCase().trim();
            return this.allLanguages.filter(l => {
                if (this.selectedCodes.includes(l.code)) return false;
                if (!term) return true;
                return l.name.toLowerCase().includes(term);
            });
        },

        selectLanguage(code) {
            if (this.onSelectCallback) {
                this.onSelectCallback(code);
            }
            this.close();
        }
    };
}
